import { useCallback, useEffect, useRef, useState } from "react";
import { invalidPackagesCount } from "@/features/ordini/packagesEditing";
import type { EditablePackage } from "@/features/ordini/packagesEditing";
import { ApiError, prettifyApiError } from "@/shared/api";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import type { AddressForm, ReturnRecord } from "../api";
import { aggiornaReso, emettiEtichettaReso, fetchReturnPrefill } from "../api";
import { AvvisaClienteTracking } from "./AvvisaClienteTracking";
import { CustomerAddressFields, INDIRIZZO_VUOTO } from "./CustomerAddressFields";

const TITLE_ID = "emetti-etichetta-title";

const FIELD_LABELS: Array<{ key: keyof EditablePackage; label: string }> = [
  { key: "kg", label: "kg" },
  { key: "l", label: "L cm" },
  { key: "a", label: "A cm" },
  { key: "p", label: "P cm" },
];

/** Campi che decidono DOVE arriva il pacco. Fuori restano di proposito
 *  azienda, telefono ed email: un telefono diverso non è un altro indirizzo
 *  (stessa scelta di ``statoSostituzione`` in features/ordini). */
const CAMPI_INDIRIZZO: Array<keyof AddressForm> = [
  "address",
  "city",
  "postcode",
  "state_iso",
  "country_iso",
  "name",
  "surname",
];

const norm = (v: string | undefined) => (v ?? "").trim().toLowerCase();

function indirizziDiversi(a: AddressForm, b: AddressForm): boolean {
  return CAMPI_INDIRIZZO.some((k) => norm(a[k]) !== norm(b[k]));
}

/** Riga leggibile di un indirizzo: quello che l'operatore riconosce a colpo
 *  d'occhio prima di premere un tasto irreversibile. */
function rigaIndirizzo(a: AddressForm): string {
  return [a.address, a.postcode, a.city, a.state_iso]
    .map((v) => v.trim())
    .filter(Boolean)
    .join(", ");
}

function completa(parziale: Partial<AddressForm>): AddressForm {
  return { ...INDIRIZZO_VUOTO, ...parziale };
}

export interface EmettiEtichettaDialogProps {
  /** Pratica in stato ``richiesta``: registrata al telefono, senza colli. */
  record: ReturnRecord;
  /** Etichetta emessa: il record aggiornato (ora ha tracking e PDF). Arriva
   *  quando l'operatore chiude la conferma, non all'emissione: i genitori
   *  chiudono il dialog appena lo ricevono. */
  onEmessa: (record: ReturnRecord) => void;
  onClose: () => void;
}

/**
 * Passo 2 della sostituzione in due tempi: i colli arrivano col pronto merce
 * del fornitore, e solo qui si emette l'etichetta SDA (spedizione VERA, una
 * volta sola — il backend risponde 409 al secondo tentativo).
 */
export function EmettiEtichettaDialog({ record, onEmessa, onClose }: EmettiEtichettaDialogProps) {
  const [pkgs, setPkgs] = useState<EditablePackage[]>(
    record.packages.length > 0
      ? record.packages.map((p) => ({ ...p }))
      : [{ kg: 0, l: 0, a: 0, p: 0 }],
  );
  const [recipient, setRecipient] = useState<AddressForm>(completa(record.recipient));
  const [modifica, setModifica] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Indirizzo dell'ordine: serve per la frase rossa. Arriva dal prefill, che è
  // la stessa fonte da cui era partita la richiesta.
  const [indirizzoOrdine, setIndirizzoOrdine] = useState<AddressForm | null>(null);
  // Etichetta nata: il dialog passa alla conferma, dove si chiede se avvisare
  // il cliente. Da qui ogni chiusura consegna il record al genitore.
  const [emessa, setEmessa] = useState<ReturnRecord | null>(null);
  const chiudiRef = useRef<HTMLButtonElement>(null);
  const chiudi = useCallback(
    () => (emessa ? onEmessa(emessa) : onClose()),
    [emessa, onEmessa, onClose],
  );

  useEffect(() => {
    let vivo = true;
    fetchReturnPrefill(record.order_reference, record.kind)
      .then((p) => {
        if (vivo) setIndirizzoOrdine(record.kind === "reso" ? p.sender : p.recipient);
      })
      .catch(() => {
        // Nessun confronto possibile: meglio nessuna frase che una frase
        // inventata. L'indirizzo resta comunque leggibile qui sopra.
      });
    return () => {
      vivo = false;
    };
  }, [record.order_reference, record.kind]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) chiudi();
    };
    window.addEventListener("keydown", onKey);
    chiudiRef.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [chiudi, busy]);

  function setField(index: number, field: keyof EditablePackage, value: number) {
    setPkgs((prev) =>
      prev.map((pkg, i) =>
        i === index ? { ...pkg, [field]: Number.isFinite(value) && value >= 0 ? value : 0 } : pkg,
      ),
    );
  }

  async function handleSalvaDestinatario(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const aggiornato = await aggiornaReso(record.id, { recipient });
      setRecipient(completa(aggiornato.recipient));
      setModifica(false);
    } catch (e) {
      setError(prettifyApiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleEmetti(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      setEmessa(await emettiEtichettaReso(record.id, pkgs));
    } catch (e) {
      // 409 = l'etichetta esiste già. Il messaggio grezzo («409 Conflict») non
      // dice all'operatore né cos'è successo né cosa fare.
      setError(
        e instanceof ApiError && e.status === 409
          ? "L'etichetta di questa pratica è già stata emessa: ricarica la pagina per scaricarla."
          : prettifyApiError(e),
      );
    } finally {
      setBusy(false);
    }
  }

  if (emessa) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) chiudi();
        }}
      >
        <div className="w-full max-w-md rounded-lg border border-line bg-card shadow-popover">
          <header className="border-b border-line px-5 py-4">
            <h2 id={TITLE_ID} className="font-display text-[17px] font-bold text-ink">
              Etichetta emessa ✓
            </h2>
            <p className="mt-0.5 text-[11px] text-ink-2">
              Ordine <span className="tabular-nums">{emessa.order_reference}</span>
              {emessa.tracking_numbers.length > 0 && (
                <>
                  {" · tracking "}
                  <span className="tabular-nums">{emessa.tracking_numbers.join(", ")}</span>
                </>
              )}
            </p>
          </header>
          {/* ``empty:hidden``: senza chat per l'ordine il riquadro non rende
              nulla, e resterebbe una banda bianca fra testata e tasti. */}
          <div className="px-5 py-4 empty:hidden">
            <AvvisaClienteTracking record={emessa} />
          </div>
          <footer className="flex justify-end border-t border-line px-5 py-3">
            <button
              ref={chiudiRef}
              type="button"
              onClick={chiudi}
              className="rounded-sm border border-line px-[14px] py-2 text-[13px] font-medium text-ink-2 hover:bg-paper focus:outline-none focus-visible:ring-1 focus-visible:ring-accent/40"
            >
              Chiudi
            </button>
          </footer>
        </div>
      </div>
    );
  }

  const invalidCount = invalidPackagesCount(pkgs);
  const diverso = indirizzoOrdine !== null && indirizziDiversi(recipient, indirizzoOrdine);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={TITLE_ID}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="w-full max-w-xl rounded-lg border border-line bg-card shadow-popover">
        <header className="border-b border-line px-5 py-4">
          <h2 id={TITLE_ID} className="font-display text-[17px] font-bold text-ink">
            Emetti etichetta
          </h2>
          <p className="mt-0.5 text-[11px] text-ink-2">
            Ordine <span className="tabular-nums">{record.order_reference}</span>
            {record.num_rif_interno ? ` · ${record.num_rif_interno}` : ""}
          </p>
        </header>

        <div className="max-h-[65vh] space-y-4 overflow-y-auto px-5 py-4">
          {record.note && (
            <div className="rounded-md border border-line bg-paper px-3 py-2">
              <p className="mb-0.5 text-[12px] font-semibold text-ink-2">Richiesta del cliente</p>
              <p className="text-[12.5px] leading-relaxed text-ink-2">{record.note}</p>
            </div>
          )}

          {/* Destinatario: in sola lettura, con «Modifica» finché la pratica è
              ancora una richiesta (dopo l'etichetta c'è un pacco che viaggia). */}
          {modifica ? (
            <div className="rounded-md border border-line p-3">
              <CustomerAddressFields
                label="Destinatario (cliente)"
                address={recipient}
                disabled={busy}
                onEdit={(field, value) => setRecipient((prev) => ({ ...prev, [field]: value }))}
              />
              <div className="mt-2.5 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => void handleSalvaDestinatario()}
                  disabled={busy}
                  aria-busy={busy}
                  className="rounded-sm border border-accent bg-accent-soft px-[12px] py-[6px] text-[12px] font-medium text-ink hover:bg-accent-2 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {busy ? "Salvo…" : "Salva destinatario"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setRecipient(completa(record.recipient));
                    setModifica(false);
                  }}
                  disabled={busy}
                  className="rounded-sm border border-line px-[12px] py-[6px] text-[12px] font-medium text-ink-2 hover:bg-paper disabled:opacity-50"
                >
                  Annulla modifica
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-start justify-between gap-3 rounded-md border border-line p-3">
              <div className="min-w-0">
                <p className="mb-0.5 text-[12px] font-semibold text-ink-2">Destinatario</p>
                <p className="text-[13px] font-medium text-ink">
                  {[recipient.company, `${recipient.name} ${recipient.surname}`.trim()]
                    .filter(Boolean)
                    .join(" · ") || "—"}
                </p>
                <p className="text-[12px] text-ink-2">{rigaIndirizzo(recipient) || "—"}</p>
              </div>
              <button
                type="button"
                onClick={() => setModifica(true)}
                className="shrink-0 rounded-sm border border-line px-[12px] py-[6px] text-[12px] font-medium text-ink-2 hover:bg-paper focus:outline-none focus-visible:ring-1 focus-visible:ring-accent/40"
              >
                Modifica
              </button>
            </div>
          )}

          {/* Colli: arrivano ora, col pronto merce. */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="text-[11.5px] font-medium text-ink-2">Colli</p>
              <button
                type="button"
                onClick={() => setPkgs((prev) => [...prev, { kg: 0, l: 0, a: 0, p: 0 }])}
                disabled={busy || pkgs.length >= 50}
                className="rounded-sm border border-line px-[10px] py-[4px] text-[11.5px] font-medium text-ink-2 hover:bg-paper disabled:opacity-50"
              >
                Aggiungi collo
              </button>
            </div>
            <div className="space-y-1.5">
              {pkgs.map((pkg, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="w-14 text-[11px] text-ink-2">Collo {index + 1}</span>
                  {FIELD_LABELS.map(({ key, label }) => (
                    <label key={key} className="flex items-center gap-1 text-[11px] text-ink-2">
                      <input
                        type="number"
                        min={0}
                        step={key === "kg" ? 0.1 : 1}
                        inputMode="decimal"
                        value={pkg[key]}
                        disabled={busy}
                        aria-label={`Collo ${index + 1} ${label}`}
                        onChange={(e) => setField(index, key, Number(e.target.value))}
                        className="w-[70px] rounded-sm border border-line px-2 py-1 text-[12px] tabular-nums focus:border-line-2 focus:outline-none focus-visible:ring-1 focus-visible:ring-accent/40 disabled:opacity-50"
                      />
                      {label}
                    </label>
                  ))}
                  {pkgs.length > 1 && (
                    <button
                      type="button"
                      aria-label={`Togli collo ${index + 1}`}
                      onClick={() => setPkgs((prev) => prev.filter((_, i) => i !== index))}
                      disabled={busy}
                      className="rounded-sm border border-line px-[8px] py-[4px] text-ink-2 hover:bg-paper disabled:opacity-50"
                    >
                      <svg
                        className="h-[13px] w-[13px] fill-none stroke-current stroke-[1.7] [stroke-linecap:round] [stroke-linejoin:round]"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path d="M6 6l12 12M18 6L6 18" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <footer className="border-t border-line px-5 py-3">
          <ErrorBanner message={error} className="mb-2" />
          {/* Prima di un atto irreversibile serve la FRASE, non un badge. */}
          {diverso && indirizzoOrdine && (
            <p
              data-testid="avviso-indirizzo-diverso"
              className="mb-2 text-[12.5px] font-medium leading-snug text-red"
            >
              Parte a: {rigaIndirizzo(recipient) || "indirizzo vuoto"} — NON a{" "}
              {rigaIndirizzo(indirizzoOrdine) || "indirizzo vuoto"}, che è l'indirizzo dell'ordine.
            </p>
          )}
          {invalidCount > 0 && (
            <p className="mb-2 text-[11.5px] text-red">
              {invalidCount} coll{invalidCount === 1 ? "o" : "i"} senza peso: inserisci un peso
              maggiore di zero per emettere l'etichetta.
            </p>
          )}
          <div className="flex items-center justify-end gap-2">
            <button
              ref={chiudiRef}
              type="button"
              onClick={onClose}
              disabled={busy}
              className="rounded-sm border border-line px-[14px] py-2 text-[13px] font-medium text-ink-2 hover:bg-paper disabled:cursor-not-allowed disabled:opacity-50"
            >
              Chiudi
            </button>
            <button
              type="button"
              onClick={() => void handleEmetti()}
              disabled={busy || invalidCount > 0 || modifica}
              aria-busy={busy}
              className="rounded-sm border border-violet bg-violet px-[14px] py-2 text-[13px] font-medium text-white hover:bg-violet-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? "Emissione in corso…" : "Emetti etichetta"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
